import { resolveCountryBucket } from "@screencloud/billing-client-api";
import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import {
  CurrencyCode,
  DISCOUNT_PERCENT,
  PER_DEVICE_VALUE_CENTS,
  PLAN_CONTENT,
  PlanContent,
  PlanId,
} from "../PlanBillingStep/constants";
import { resolveSelection } from "../PlanBillingStep/selection";
import {
  billingPeriodPriceCents,
  useOnboardingPlansContext,
} from "../PlanBillingStep/useOnboardingPlans";
import { ShippingRate, shippingRateFor } from "./shippingRates";
import { useShippingRates } from "./useShippingRates";

/**
 * The single pricing computation for the Pixi Bundle purchase, shared by the
 * Checkout step (which shows the shipping-inclusive grand total) and the
 * Complete step (which shows the subscription total and shipping separately).
 * Keeping it in one place stops the two screens' numbers from drifting.
 *
 * All amounts are in MINOR units (cents), matching the billing currency util.
 * When the customer-create response becomes the source of truth, repoint the
 * totals here rather than in each screen.
 */
export interface CheckoutPricing {
  planId: PlanId;
  planContent: PlanContent;
  currency: CurrencyCode;
  billingCycle: "year" | "month";
  licenses: number;
  shippingCountry: string;
  isFreeDevice: boolean;
  isDiscount: boolean;
  licencesCents: number;
  deviceSavingsCents: number;
  discountCents: number;
  shippingCents: number;
  /**
   * The shipping service actually available for the destination, taken from the
   * resolved rate (a destination may offer only express, or only standard) —
   * never assumed. undefined when no rate matches (no shipping available).
   */
  shippingService?: string;
  /** Licences minus any discount — EXCLUDES shipping. */
  subscriptionTotalCents: number;
  /** Subscription total plus shipping (free-device deal only). */
  grandTotalCents: number;
  rates: ShippingRate[];
}

export const useCheckoutPricing = (
  state: PixiWizardSelection,
): CheckoutPricing => {
  const { getPlan, isProTrialist } = useOnboardingPlansContext();
  const { rates } = useShippingRates();

  const { planId, currency, billingCycle, shippingCountry, licenses } =
    resolveSelection(state, isProTrialist);
  const plan = getPlan(planId, billingCycle, currency);
  const monthlyPlan = getPlan(planId, "month", currency);

  const isFreeDevice = state.dealId === "free-device";
  const isDiscount = state.dealId === "discount";

  // Yearly + free-device bills at the monthly rate × 12 (the free Pixi replaces
  // the annual discount) — see `billingPeriodPriceCents` (GRWBKK-1695).
  const licencesCents = plan
    ? billingPeriodPriceCents(plan, monthlyPlan, isFreeDevice) * licenses
    : 0;
  const deviceSavingsCents = PER_DEVICE_VALUE_CENTS[currency] * licenses;
  const discountCents = Math.round((licencesCents * DISCOUNT_PERCENT) / 100);
  // `shippingCountry` is an ISO code from the dropdown; the API keys rates by
  // regional bucket, so translate before looking up the rate. `resolveCountryBucket`
  // (billing client) returns `null` for an unrecognised code — treat that as no
  // bucket so no rate matches (see GRWBKK SC-08).
  // Resolve the actual rate row: the user's picked service is only a preference —
  // the destination decides what's available (standard, express, or neither), so
  // the charged amount and the service both come from the matched rate, never a
  // hardcoded default.
  const shippingRate = shippingRateFor(rates, {
    countryBucket: resolveCountryBucket(shippingCountry) ?? undefined,
    currency,
    screenCount: licenses,
    shippingService: state.shippingService,
  });
  const shippingCents = shippingRate ? shippingRate.amount * 100 : 0;
  const shippingService = shippingRate?.service;

  // Yearly + discount bills at the annual catalogue price — the ~17% annual
  // saving is already baked into that price. Only monthly + discount subtracts
  // the deal discount again (GRWBKK-1705).
  const applyDealDiscount = isDiscount && billingCycle === "month";
  const subscriptionTotalCents =
    licencesCents - (applyDealDiscount ? discountCents : 0);
  const grandTotalCents =
    subscriptionTotalCents + (isFreeDevice ? shippingCents : 0);

  return {
    planId,
    planContent: PLAN_CONTENT[planId],
    currency,
    billingCycle,
    licenses,
    shippingCountry,
    isFreeDevice,
    isDiscount,
    licencesCents,
    deviceSavingsCents,
    discountCents,
    shippingCents,
    shippingService,
    subscriptionTotalCents,
    grandTotalCents,
    rates,
  };
};
